import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import { useTheme, alpha } from "@mui/material/styles";

/**
 * Full-viewport glassmorphism shell for the auth pages (sign-in / sign-up /
 * register). Replaces the legacy MD2 BasicLayout/CoverLayout (DefaultNavbar +
 * full-bleed photo background) with a brand-gradient backdrop and a frosted
 * glass card.
 */
function GlassLayout({ children, maxWidth = "sm", eyebrow }) {
  const theme = useTheme();
  const teal = theme.palette.teal;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 2,
        background: `linear-gradient(135deg, ${teal[800]} 0%, ${teal[600]} 45%, ${teal[300]} 100%)`,
      }}
    >
      {/* Decorative blurred blobs */}
      <Box
        sx={{
          position: "absolute",
          top: -140,
          left: -120,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: alpha(teal[200], 0.45),
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -160,
          right: -140,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: alpha("#ffffff", 0.18),
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 3 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#ffffff", 0.2),
              border: `1px solid ${alpha("#ffffff", 0.35)}`,
            }}
          >
            <Icon sx={{ color: "white" }}>church</Icon>
          </Box>
          <Typography variant="h5" sx={{ color: "white", fontWeight: 700 }}>
            Ecclesia
          </Typography>
        </Box>
        {eyebrow && (
          <Typography
            variant="body2"
            sx={{ color: alpha("#ffffff", 0.85), mb: 2, textAlign: "center" }}
          >
            {eyebrow}
          </Typography>
        )}

        <Container disableGutters maxWidth={maxWidth} sx={{ width: "100%" }}>
          <Box
            sx={{
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              background: alpha("#ffffff", 0.72),
              border: `1px solid ${alpha("#ffffff", 0.5)}`,
              borderRadius: 4,
              boxShadow: "0 24px 64px rgba(5, 30, 25, 0.35)",
              overflow: "hidden",
            }}
          >
            {children}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

GlassLayout.propTypes = {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  eyebrow: PropTypes.string,
};

export default GlassLayout;
